import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const PLANS = [
    { id: 'optimum_global_emerald',  key: 'emerald',  label: 'Emerald' },
    { id: 'optimum_global_sapphire', key: 'sapphire', label: 'Sapphire' },
    { id: 'optimum_global_ruby',     key: 'ruby',     label: 'Ruby' },
    { id: 'optimum_global_jade',     key: 'jade',     label: 'Jade' },
    { id: 'optimum_global_diamond',  key: 'diamond',  label: 'Diamond' },
];

const ADD_ONS = [
    { key: 'dental',    label: 'Dental',    premiumField: 'dental_premium' },
    { key: 'maternity', label: 'Maternity', premiumField: 'maternity_premium' },
    { key: 'optical',   label: 'Optical',   premiumField: 'optical_premium' },
];

// Same options as OptimumGlobal_Rates
const CURRENCIES = ['USD', 'EURO', 'GBP'];
const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly'];
const BRAND_COLOR = '#009999';
const ADD_ON_ROW_COLOR = '#424242';

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as OptimumGlobal_Rates: discount/loading scale the plan rate including its add-ons
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

// Maternity only applies to female clients aged 18-64
function isMaternityEligible(client) {
    return client?.gender === 'Female' && client?.age >= 18 && client?.age <= 64;
}

function formatPercent(value) {
    if (typeof value !== 'number' || isNaN(value)) return '';
    return `${parseFloat((value * 100).toFixed(2))}%`;
}

export default function OptimumGlobal_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const specifics = appConfig?.content?.specifics?.optimum_global || {};
    const areaOptions        = appConfig?.content?.optimum_global_area_of_coverage || [];
    const defaultArea = areaOptions.includes('Worldwide excluding USA') ? 'Worldwide excluding USA' : (areaOptions[0] || '');
    const familyDiscounts    = specifics.family_discount    || {};
    const mhdGroupLoadings   = specifics.mhd_group_loadings || {};
    const bmiLoadings        = specifics.bmi_loadings       || {};
    const businessTypeOptions   = Object.keys(specifics.business_type || {});
    const quoteTypeOptions      = specifics.quote_type || [];
    const familyDiscountOptions = Object.keys(familyDiscounts);
    const underwritingOptions   = Object.keys(mhdGroupLoadings);
    const bmiLoadingOptions     = Object.keys(bmiLoadings);
    const outpatientCoPayOptions = Object.keys(specifics.out_patient_co_pay || {});
    const getDeductibleOptions = (curr) => appConfig?.content?.deductibles?.optimum_global?.[curr] || [];

    const [selectedPlan, setSelectedPlan] = useState('optimum_global_emerald');
    const [selectedAddOns, setSelectedAddOns] = useState(new Set());
    // { [clientIndex]: { dental?: bool, maternity?: bool, optical?: bool } } — unset means the default
    const [addOnsPerClient, setAddOnsPerClient] = useState({});
    // Plan options (global — same for all quotes)
    const [businessType, setBusinessType]   = useState('');
    const [quoteType, setQuoteType]         = useState('');
    const [familyDiscount, setFamilyDiscount] = useState('');
    const [underwriting, setUnderwriting]   = useState('');
    const [bmiLoading, setBmiLoading]       = useState('');
    // Options
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency]             = useState('USD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');

    const deductibleOptions = getDeductibleOptions(currency);
    // { [quoteName]: { deductible: '', outpatient_co_pay: '' } }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    // { [quoteName]: { deductible: true, outpatient_co_pay: true } }
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj = PLANS.find(p => p.id === selectedPlan) || PLANS[0];
    const activeAddOns = ADD_ONS.filter(a => selectedAddOns.has(a.key));

    function getDefaultQuoteDeductibles() {
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = {
                deductible:      deductibleOptions[0]      || '',
                outpatient_co_pay: outpatientCoPayOptions[0] || '',
            };
        });
        return defaults;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const savedAddOns = editingPlan.payloads?.add_ons || [];
            setSelectedPlan(editingPlan.base_plan_id || editingPlan.plan_id);
            setSelectedAddOns(new Set(savedAddOns));
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'USD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setBusinessType(editingPlan.payloads?.business_type   || businessTypeOptions[0]   || '');
            setQuoteType(editingPlan.payloads?.quote_type         || quoteTypeOptions[0]       || '');
            setFamilyDiscount(editingPlan.payloads?.family_discount || familyDiscountOptions[0] || '');
            setUnderwriting(editingPlan.payloads?.underwriting    || underwritingOptions[0]    || '');
            setBmiLoading(editingPlan.payloads?.bmi_loading       || bmiLoadingOptions[0]      || '');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);

            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid]
                    || { deductible: '', outpatient_co_pay: '' };
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from stored premiums
            const ratesMap = {};
            const perClient = {};
            const planKey = editingPlan.plan;
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const matchesEditingPlan = p => editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id;
                const premiums = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    return clientPremium?.premiums?.find(matchesEditingPlan);
                });
                const rawRates = premiums.map(premium =>
                    Object.fromEntries(PLANS.map(plan => [plan.key, unscale(premium?.[`${plan.key}_premium`] ?? null)]))
                );
                // Add-on rates are plan-level (same for every client), but only clients the add-on
                // was applied to have it saved — take the first saved value, and treat clients
                // without one as unticked.
                const optionalRates = {};
                ADD_ONS.forEach(addon => {
                    const saved = premiums.map(p => p?.[addon.premiumField]).find(v => typeof v === 'number');
                    optionalRates[addon.key] = { [planKey]: unscale(saved ?? null) };
                    if (!savedAddOns.includes(addon.key)) return;
                    premiums.forEach((premium, ci) => {
                        perClient[ci] = { ...(perClient[ci] || {}), [addon.key]: typeof premium?.[addon.premiumField] === 'number' };
                    });
                });
                ratesMap[quote.uid] = { rawRates, optionalRates };
            });
            setAddOnsPerClient(perClient);
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open && !editingPlan) {
            // Set defaults on fresh open
            setSelectedAddOns(new Set());
            setAddOnsPerClient({});
            setBusinessType(businessTypeOptions[0]   || '');
            setQuoteType(quoteTypeOptions[0]          || '');
            setFamilyDiscount(familyDiscountOptions[0] || '');
            setUnderwriting(underwritingOptions[0]    || '');
            setBmiLoading(bmiLoadingOptions[0]        || '');
            setAreaOfCoverage(defaultArea);
            setDiscount('0');
            setLoadingPercent('0');
            setQuoteDeductibles(getDefaultQuoteDeductibles());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Same as OptimumGlobal_Rates: family discount only applies to Individual business
    useEffect(() => {
        if (initializingFromEdit.current) return;
        if (businessType && businessType !== 'Individual' && familyDiscountOptions.includes('N/A')) {
            setFamilyDiscount('N/A');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessType]);

    // Add-on rates for every plan come back with the plan rates, so toggling an add-on just
    // shows a different set of rows — no refresh needed.
    function toggleAddOn(key) {
        setSelectedAddOns(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    // Same defaults as OptimumGlobal_Rates' checkAddOnPerClient
    function isAddOnIncludedForClient(addonKey, clientIndex) {
        const isChecked = addOnsPerClient[clientIndex]?.[addonKey];
        if (isChecked !== undefined) return isChecked;
        if (addonKey === 'maternity') return isMaternityEligible(clients[clientIndex]);
        return true;
    }

    function toggleAddOnForClient(addonKey, clientIndex) {
        const current = isAddOnIncludedForClient(addonKey, clientIndex);
        setAddOnsPerClient(prev => ({
            ...prev,
            [clientIndex]: { ...(prev[clientIndex] || {}), [addonKey]: !current },
        }));
    }

    function setQuoteField(quoteName, field, value) {
        setQuoteDeductibles(prev => ({
            ...prev,
            [quoteName]: { ...(prev[quoteName] || {}), [field]: value },
        }));
        setDeductibleErrors(prev => ({
            ...prev,
            [quoteName]: { ...(prev[quoteName] || {}), [field]: false },
        }));
        invalidateRates();
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name]?.deductible === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (isBlankQuote(q)) return;
            const d = quoteDeductibles[q.name];
            const qErrors = {};
            if (!d?.deductible) qErrors.deductible = true;
            if (!d?.outpatient_co_pay) qErrors.outpatient_co_pay = true;
            if (Object.keys(qErrors).length > 0) errors[q.name] = qErrors;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please complete deductibles for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { optimum_global: quoteDeductibles[quote.name]?.deductible || '' },
                            insurers: ['optimum_global'],
                            specifics: {
                                optimum_global: {
                                    business_type:    businessType,
                                    quote_type:       quoteType,
                                    family_discount:  familyDiscount,
                                    mhd_group_loading: underwriting,
                                    bmi_loading:      bmiLoading,
                                    out_patient_co_pay: quoteDeductibles[quote.name]?.outpatient_co_pay || '',
                                    inpatient_deductible: quoteDeductibles[quote.name]?.deductible || '',
                                },
                            },
                            specific_area_of_coverage:   { optimum_global: areaOfCoverage },
                            rates_version_per_insurer:   { optimum_global: selectedRatesVersion?.optimum_global || '' },
                            specific_currency:           { optimum_global: currency },
                            specific_payment_frequency:  { optimum_global: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.optimum_global }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(PLANS.map(plan => [plan.key, r[plan.key]])));
                    ratesMap[quoteUid] = { rawRates, optionalRates: data.optional_rates || {} };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Optimum Global rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // ratesData keeps the raw API rates; discount/loading are applied on read
    function getPlanRate(clientIndex, planKey, quoteUid) {
        const rate = ratesData?.[quoteUid]?.rawRates?.[clientIndex]?.[planKey];
        if (rate === undefined || rate === null) return null;
        return typeof rate === 'number' ? rate * rateFactor : rate;
    }

    // Add-on rate for a client — plan-level (same for every client), only when the add-on is
    // selected, ticked for that client, and (for maternity) the client is eligible
    function getAddOnRate(clientIndex, addonKey, quoteUid) {
        if (!selectedAddOns.has(addonKey)) return null;
        if (!isAddOnIncludedForClient(addonKey, clientIndex)) return null;
        if (addonKey === 'maternity' && !isMaternityEligible(clients[clientIndex])) return null;
        const rate = ratesData?.[quoteUid]?.optionalRates?.[addonKey]?.[selectedPlanObj.key];
        return typeof rate === 'number' ? rate * rateFactor : null;
    }

    // Total = selected plan + active add-ons
    function getRate(clientIndex, quoteUid) {
        const planRate = getPlanRate(clientIndex, selectedPlanObj.key, quoteUid);
        let total = 0;
        let hasAny = false;
        if (typeof planRate === 'number') { total += planRate; hasAny = true; }
        for (const addon of activeAddOns) {
            const r = getAddOnRate(clientIndex, addon.key, quoteUid);
            if (typeof r === 'number') { total += r; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function sumClients(getter) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getter(i);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function buildDeductibleString(quoteName) {
        const d = quoteDeductibles[quoteName];
        const deductible = d?.deductible || '';
        const coPay = d?.outpatient_co_pay || '';
        if (!deductible) return '';
        return coPay ? `${deductible} | OP Co-Pay: ${coPay}` : deductible;
    }

    function buildPlanLabel() {
        let label = selectedPlanObj.label;
        if (activeAddOns.length > 0) {
            label += ' + ' + activeAddOns.map(a => a.label).join(' + ');
        }
        return label;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const addOnsSuffix = [...selectedAddOns].sort().join('_');
        const effectivePlanId = editingPlan
            ? editingPlan.plan_id
            : (addOnsSuffix ? `${selectedPlan}_${addOnsSuffix}` : selectedPlan);
        // plan_id identifies the tier + add-ons combination and is shared by every instance of it;
        // uid identifies this specific plan instance so multiple plans of the same combination
        // can coexist instead of overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const planKey = selectedPlanObj.key;
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getRate(clientIndex, quote.uid);
                // Stored with discount/loading applied, matching premium_rate
                const planPremiums = Object.fromEntries(PLANS.map(plan => [
                    `${plan.key}_premium`,
                    getPlanRate(clientIndex, plan.key, quote.uid),
                ]));
                const addOnPremiums = Object.fromEntries(ADD_ONS.map(addon => [
                    addon.premiumField,
                    getAddOnRate(clientIndex, addon.key, quote.uid),
                ]));

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: effectivePlanId,
                    planUid,
                    premium: {
                        insurer: 'optimum_global',
                        plan: planKey,
                        plan_label: planLabel,
                        included_benefits: '',
                        deductible: buildDeductibleString(quote.name),
                        area_of_coverage: areaOfCoverage,
                        currency,
                        payment_frequency: paymentFrequency,
                        add_ons: [...selectedAddOns],
                        discount,
                        loading: loadingPercent,
                        premium_rate: rate ?? null,
                        ...planPremiums,
                        ...addOnPremiums,
                    },
                });
            });
        });

        const plan = {
            plan_id:      effectivePlanId,
            uid:          planUid,
            base_plan_id: selectedPlan,
            plan:         planKey,
            insurer:      'optimum_global',
            label: `AXA Optimum Global ${planLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                business_type:  businessType,
                quote_type:     quoteType,
                family_discount: familyDiscount,
                underwriting,
                bmi_loading:    bmiLoading,
                add_ons:        [...selectedAddOns],
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || {}])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    // Plans saved before the options matched OptimumGlobal_Rates can hold SGD / Monthly —
    // keep showing that saved value instead of an empty select
    const currencyOptions = CURRENCIES.includes(currency) ? CURRENCIES : [...CURRENCIES, currency];
    const paymentFrequencyOptions = PAYMENT_FREQUENCIES.includes(paymentFrequency) ? PAYMENT_FREQUENCIES : [...PAYMENT_FREQUENCIES, paymentFrequency];

    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };
    const valueSx = { fontSize: 13, color: BRAND_COLOR, fontWeight: 600 };

    // Percent shown next to a select, same as OptimumGlobal_Rates
    function renderSelectRow(label, value, onChange, options, percentLabel, extra = null) {
        if (options.length === 0) return null;
        return (
            <>
                <Typography fontWeight={600} sx={{ fontSize: 13, lineHeight: '32px' }}>{label}</Typography>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select value={value} disabled={loading} onChange={onChange} sx={selectSx}>
                                {options.map(o => <MenuItem key={o} value={o} sx={menuItemSx}>{o}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {percentLabel && <Typography sx={valueSx}>{percentLabel}</Typography>}
                    </Box>
                    {extra}
                </Box>
            </>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: BRAND_COLOR,
                    backgroundColor: '#fff',
                    width: 100,
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.2,
                }}>Optimum Global</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/Optimum Global Logo.png`} alt="Optimum Global" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                <Box>
                    {/* Plan Type */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={selectedPlan}
                                disabled={loading || !!editingPlan}
                                // Rates for every plan come back in one fetch, so switching plans just shows a different row
                                onChange={(e) => setSelectedPlan(e.target.value)}
                                sx={selectSx}
                            >
                                {PLANS.map(plan => (
                                    <MenuItem key={plan.id} value={plan.id} sx={menuItemSx}>{plan.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Add ons */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 15 }}>Add ons</Typography>
                        {ADD_ONS.map(addon => (
                            <FormControlLabel
                                key={addon.key}
                                sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedAddOns.has(addon.key)}
                                        onChange={() => toggleAddOn(addon.key)}
                                        disabled={loading || !!editingPlan}
                                    />
                                }
                                label={addon.label}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Plan Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'start', alignContent: 'start', mb: 2 }}>
                        {renderSelectRow('Business Type', businessType, (e) => { setBusinessType(e.target.value); invalidateRates(); }, businessTypeOptions)}
                        {renderSelectRow('Quote Type', quoteType, (e) => { setQuoteType(e.target.value); invalidateRates(); }, quoteTypeOptions)}
                        {renderSelectRow(
                            'Family Discount',
                            familyDiscount,
                            (e) => { setFamilyDiscount(e.target.value); invalidateRates(); },
                            familyDiscountOptions,
                            typeof familyDiscounts[familyDiscount] === 'number' ? formatPercent(1 - familyDiscounts[familyDiscount]) : '',
                            familyDiscount === 'Family of 3 or more' && clients.length < 3 && (
                                <Typography fontSize={11} color="#f44336" sx={{ mt: 0.5 }}>
                                    Applicable only for 3 or more clients
                                </Typography>
                            ),
                        )}
                        {renderSelectRow('Underwriting', underwriting, (e) => { setUnderwriting(e.target.value); invalidateRates(); }, underwritingOptions, formatPercent(mhdGroupLoadings[underwriting]))}
                        {renderSelectRow('BMI Loadings', bmiLoading, (e) => { setBmiLoading(e.target.value); invalidateRates(); }, bmiLoadingOptions, formatPercent(bmiLoadings[bmiLoading]))}
                        {renderSelectRow('Area of Coverage', areaOfCoverage, (e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }, areaOptions)}
                        {renderSelectRow('Currency', currency, (e) => {
                            const nextCurrency = e.target.value;
                            const firstDeductible = getDeductibleOptions(nextCurrency)[0] || '';
                            setCurrency(nextCurrency);
                            // Deductible options change per currency — reset to the new currency's
                            // first option, keeping any "Leave Blank" quotes blank
                            setQuoteDeductibles(prev => {
                                const next = {};
                                Object.keys(prev).forEach(k => {
                                    next[k] = {
                                        deductible: prev[k]?.deductible === LEAVE_BLANK ? LEAVE_BLANK : firstDeductible,
                                        outpatient_co_pay: prev[k]?.outpatient_co_pay || '',
                                    };
                                });
                                return next;
                            });
                            invalidateRates();
                        }, currencyOptions)}
                        {renderSelectRow('Payment Frequency', paymentFrequency, (e) => { setPaymentFrequency(e.target.value); invalidateRates(); }, paymentFrequencyOptions)}
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Deductibles per Quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{quote.name}</Typography>
                                <Box sx={{ display: 'flex', gap: 2, pl: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 70 }}>Inpatient Deductible</Typography>
                                        <CustomSelect2
                                            dense
                                            value={quoteDeductibles[quote.name]?.deductible || ''}
                                            onChange={(e) => setQuoteField(quote.name, 'deductible', e.target.value)}
                                            items={[LEAVE_BLANK, ...deductibleOptions]}
                                            disabled={loading}
                                            error={!!deductibleErrors[quote.name]?.deductible}
                                            sx={{ minWidth: 200 }}
                                        />
                                    </Box>
                                    {outpatientCoPayOptions.length > 0 && !isBlankQuote(quote) && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 100 }}>Outpatient Co-Pay Option</Typography>
                                            <CustomSelect2
                                                dense
                                                value={quoteDeductibles[quote.name]?.outpatient_co_pay || ''}
                                                onChange={(e) => setQuoteField(quote.name, 'outpatient_co_pay', e.target.value)}
                                                items={outpatientCoPayOptions}
                                                disabled={loading}
                                                error={!!deductibleErrors[quote.name]?.outpatient_co_pay}
                                                sx={{ minWidth: 180 }}
                                                isZeroDeductibleUniform={false}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {/* Rates Display */}
                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => {
                            const d = quoteDeductibles[quote.name];
                            const summary = isBlankQuote(quote) ? LEAVE_BLANK : [d?.deductible, d?.outpatient_co_pay ? `OP Co-Pay: ${d.outpatient_co_pay}` : ''].filter(Boolean).join(' | ');
                            return (
                                <Box key={quote.name} sx={{ mb: 3 }}>
                                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                                        {quote.name}
                                        <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                            — {summary}
                                        </Typography>
                                    </Typography>
                                    {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                        <Table size="small">
                                            <TableHead>
                                                {/* Add On Per Client — same per-client toggles as OptimumGlobal_Rates.
                                                    Add-on rates are already loaded, so toggling doesn't need a refresh. */}
                                                {activeAddOns.length > 0 && (
                                                    <TableRow sx={{ backgroundColor: ADD_ON_ROW_COLOR }}>
                                                        <TableCell sx={{ color: '#fff', textAlign: 'right', fontSize: 13 }}>Add On Per Client</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {activeAddOns.map(addon => (
                                                                    <div key={addon.key}>
                                                                        <FormControlLabel
                                                                            sx={{ mb: 0, color: '#fff', whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                                                                            control={
                                                                                <Checkbox
                                                                                    size="small"
                                                                                    checked={isAddOnIncludedForClient(addon.key, idx)}
                                                                                    disabled={loading || (addon.key === 'maternity' && !isMaternityEligible(client))}
                                                                                    onChange={() => toggleAddOnForClient(addon.key, idx)}
                                                                                    sx={{ py: 0, pl: 0, color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' } }}
                                                                                />
                                                                            }
                                                                            label={addon.label}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell />
                                                    </TableRow>
                                                )}
                                                <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 190 }}>Plan</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                            <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                            <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {/* Base plan row */}
                                                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{selectedPlanObj.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getPlanRate(idx, selectedPlanObj.key, quote.uid), currency)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(sumClients(i => getPlanRate(i, selectedPlanObj.key, quote.uid)), currency)}
                                                    </TableCell>
                                                </TableRow>
                                                {/* Add-on rows */}
                                                {activeAddOns.map((addon, addonIdx) => (
                                                    <TableRow key={addon.key} sx={{ backgroundColor: (addonIdx + 1) % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                        <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{addon.label}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(getAddOnRate(idx, addon.key, quote.uid), currency)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                            {formatRate(sumClients(i => getAddOnRate(i, addon.key, quote.uid)), currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Total row — shown when add-ons selected */}
                                                {activeAddOns.length > 0 && (
                                                    <TableRow sx={{ backgroundColor: '#e0f2f1' }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>{buildPlanLabel()}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                                {formatRate(getRate(idx, quote.uid), currency)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 700 }}>
                                                            {formatRate(sumClients(i => getRate(i, quote.uid)), currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </Paper>
)}
                                </Box>
                            );
                        })}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="optimum_global" />

                <RatesVersionSelect
                    insurer="optimum_global"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
